import React from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {Popover} from '@mui/material';
import {observer} from 'mobx-react';

import ConditionalComponent from '../Shared/ConditionalComponent';

import {useSelectModalState} from './GenericSelectSearchModal/useSelectModalState';
import {SearchBar} from './GenericSelectSearchModal/SearchBar';
import {SelectList} from './GenericSelectSearchModal/SelectList';
import {SelectModalActionBar} from './GenericSelectSearchModal/ActionBar';

export const GenericSelectSearchModal = observer(({useStores}) => {
    const {modalStore} = useStores();
    const state = useSelectModalState(modalStore);
    const {data, searchString, isServerMode} = state;

    const onClose = data.showActionBar ? state.handleCancel : state.closeModal;

    const popoverProps = data.anchorEl
        ? {
            anchorEl: data.anchorEl,
            anchorOrigin: data.anchorOrigin || {vertical: 'bottom', horizontal: 'left'},
            transformOrigin: data.transformOrigin || {vertical: 'top', horizontal: 'left'},
        }
        : {
            anchorReference: 'none',
            transformOrigin: {vertical: 'bottom', horizontal: 'right'},
            style: {top: data.position.y ?? '100px', left: data.position.x ?? '100px'},
        };

    const paperClass = data.anchorEl
        ? 'rounded-lg shadow-xl'
        : 'absolute border-panels-normal rounded-1 h-56 w-56 bg-white z-50';

    const paperStyle = data.anchorEl ? {width: 330, height: '60vh', maxHeight: '80vh'} : {};

    const handleSearch = (value) => {
        isServerMode ? state.handleServerSearch(value) : state.setSearchString(value);
    };

    const handleClear = () => {
        isServerMode ? state.handleServerSearch('') : state.setSearchString('');
    };

    return (
        <Popover
            open={!!modalStore.isGenericSelectSearchModalOpen}
            onClose={onClose}
            PaperProps={{className: paperClass, style: paperStyle}}
            {...popoverProps}
        >
            <div className={`flex flex-col ${data.anchorEl ? 'h-full bg-windowsandmenus-normal text-sm' : 'absolute rounded-menu bg-white shadow-lg font-sans text-xs h-full w-full p-3'}`}>
                <ConditionalComponent if={data.title}>
                    <div className="flex items-center justify-between px-4 pt-3 pb-2">
                        <span className="text-sm font-bold text-main-normal">{data.title}</span>
                        <div className="flex items-center gap-2">
                            <ConditionalComponent if={data.headerAction}>
                                <span
                                    className="text-xs text-classic-button-normal cursor-pointer hover:underline"
                                    onClick={data.headerAction?.onClick}
                                >
                                    {data.headerAction?.label}
                                </span>
                            </ConditionalComponent>
                            <CloseIcon
                                onClick={onClose}
                                className="cursor-pointer"
                                style={{fontSize: 18, color: 'var(--main-disabled-color)'}}
                            />
                        </div>
                    </div>
                </ConditionalComponent>

                <SearchBar
                    data={data}
                    searchString={searchString}
                    isServerMode={isServerMode}
                    onSearch={handleSearch}
                    onClear={handleClear}
                />

                <SelectList
                    data={data}
                    list={state.list}
                    searchString={searchString}
                    selectedOptions={state.selectedOptions}
                    isServerMode={isServerMode}
                    isFetching={state.isFetching}
                    onScroll={state.handleScroll}
                    onItemClick={state.handleOnClick}
                />

                <ConditionalComponent if={data.showActionBar || data.showSelectedCount}>
                    <SelectModalActionBar
                        totalSelected={data.totalSelected ?? state.selectedOptions.length}
                        onCancel={state.handleCancel}
                        onSave={state.handleSave}
                        hideActions={!data.showActionBar}
                    />
                </ConditionalComponent>
            </div>
        </Popover>
    );
});
